import { useWatch } from "@web-monorepo/hooks";
import { useRef, useState } from "react";
import { useLocation } from "react-router";
import Zendesk, { ZendeskAPI } from "react-zendesk";
import { getEntityType } from "#/src/hooks/api/auth/session";
import useHelperWidgetAPI, { WindowWithHelperAPIMethods, callDecagonMethod } from "#/src/hooks/useHelperWidgetAPI";
import { useSessionQuery } from "#/src/hooks/useSession";
import { logTutorEvent } from "#/src/utils/log";
import { shouldHideWidget } from "./helperWidgetVisibility";
// eslint-disable-next-line @web-monorepo/no-css-file-imports
import "./helper-widget-overrides.css";

const zendeskSettings = {
  contactForm: {
    selectTicketForm: {
      "*": "I need help as a ...",
    },
  },
};

type ZendesScriptProps = {
  setIsLoaded: (_isLoaded: boolean) => void;
};

const ZendeskScript = ({ setIsLoaded }: ZendesScriptProps) => {
  // Config.zendeskWebWidgetKey is `string | null` — skip rendering rather than
  // passing an empty key to Zendesk (which would silently init with a blank
  // widget). The `null` case corresponds to envs without Zendesk configured.
  if (!Config.zendeskWebWidgetKey) return null;
  return (
    <Zendesk
      zendeskKey={Config.zendeskWebWidgetKey}
      {...zendeskSettings}
      onLoaded={() => {
        ZendeskAPI("webWidget", "hide");
        setIsLoaded(true);
      }}
    />
  );
};

type DecagonScriptProps = {
  setIsLoaded: (_isLoaded: boolean) => void;
};

const DecagonScript = ({ setIsLoaded }: DecagonScriptProps) => {
  const scriptWrapperRef = useRef<HTMLDivElement>(null);
  const HelperWidgetAPI = useHelperWidgetAPI();

  useWatch([scriptWrapperRef.current], () => {
    if (scriptWrapperRef.current) {
      const script = document.createElement("script");
      script.src = "https://decagon.ai/loaders/classdojo.js";
      scriptWrapperRef.current.appendChild(script);
      script.addEventListener("load", () => {
        callDecagonMethod(() => (window as WindowWithHelperAPIMethods).duet?.hide());
        setIsLoaded(true);
        HelperWidgetAPI.setDecagonEventListener();
      });
    }
  });
  return <div ref={scriptWrapperRef}></div>;
};

const HelperWidget = () => {
  // Without a Zendesk key (local dev / Playwright), react-zendesk would render a
  // <script src=".../snippet.js?key=null"> that throws a page-level TypeError.
  // Skip rendering <ZendeskScript> entirely and treat Zendesk as "loaded" so the
  // rest of the HelperWidget lifecycle proceeds.
  const [isZendeskLoaded, setIsZendeskLoaded] = useState(!Config.zendeskWebWidgetKey);
  const [isDecagonLoaded, setIsDecagonLoaded] = useState(false);
  const helperWidgetAPI = useHelperWidgetAPI();
  const { pathname } = useLocation();
  const { sessionData, isFetching } = useSessionQuery();

  useWatch([isZendeskLoaded, isDecagonLoaded, pathname, helperWidgetAPI, sessionData, isFetching], () => {
    const shouldShow = isZendeskLoaded && isDecagonLoaded && !shouldHideWidget(pathname) && !isFetching;

    if (shouldShow) {
      if (sessionData) {
        const userType = getEntityType(sessionData.roles);
        helperWidgetAPI.setWidgetUserData({
          userId: sessionData.user_details.uid,
          email: sessionData.user_details.email || "",
          userType,
          fullname: `${sessionData.user_details.first_name || ""} ${sessionData.user_details.last_name || ""}`,
          caller: "helperWidgetWatcher",
        });
        logTutorEvent({
          eventName:
            userType === "parent"
              ? "parent.marketplace.decagon_widget_shown"
              : "tutor.marketplace.decagon_widget_shown",
          metadata: { email: sessionData.user_details.email },
        });
      }

      helperWidgetAPI.showWidget();
    }
  });

  return (
    <>
      <DecagonScript setIsLoaded={setIsDecagonLoaded} />
      {Config.zendeskWebWidgetKey && <ZendeskScript setIsLoaded={setIsZendeskLoaded} />}
    </>
  );
};

interface HelperProviderProps {
  children: React.ReactNode;
}

export default function HelperWidgetProvider({ children }: HelperProviderProps) {
  return (
    <>
      <HelperWidget />
      {children}
    </>
  );
}
